import { Add } from '@carbon/react/icons';
import {
  Button,
  DataTable,
  DataTableRenderProps,
  InlineNotification,
  Loading,
  Select,
  SelectItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@carbon/react';
import { PageHeader } from 'components/page-header';
import { useAtomValue, useSetAtom } from 'jotai';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useHistory } from 'react-router-dom';
import { pageTitle } from 'utils/page-title';
import routes from '~/routes';
import {
  accountsAtom,
  BULK_IMPORT_URL,
  bulkImportAtom,
  EnergyLinkAccount,
} from '../atoms';
import { EnergyLinkAccessCheck } from '../energy-link-access-check';
import styles from './accounts-page.module.scss';

interface DataTableHeader {
  key: string;
  header: string;
}

interface DataTableRow {
  id: string;
  username: string;
  actions: JSX.Element;
}

interface DataTableCell {
  id: string;
  value: React.ReactNode;
}

const headers: DataTableHeader[] = [
  { header: 'Username', key: 'username' },
  { header: 'Actions', key: 'actions' },
];

const EnergyLinkAccountsPage = () => {
  const history = useHistory();
  const [error, setError] = useState<string | null>(null);
  const accounts = useAtomValue(accountsAtom);
  const bulkImport = useSetAtom(bulkImportAtom);
  const [selectedDays, setSelectedDays] = useState<Record<string, number>>({});
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>(
    {}
  );

  const handleDaysChange = (accountId: number, days: number) => {
    setSelectedDays((prev) => ({
      ...prev,
      [accountId]: days,
    }));
  };

  const handleImport = (account: EnergyLinkAccount) => {
    const days = selectedDays[account.id] || 90;
    setLoadingStates((prev) => ({ ...prev, [account.id]: true }));
    setError(null);

    bulkImport({
      url: BULK_IMPORT_URL,
      type: 'POST',
      data: {
        accountId: account.id.toString(),
        days,
      },
      onSuccess: () => {
        setLoadingStates((prev) => ({ ...prev, [account.id]: false }));
      },
      onError: () => {
        setError(`Failed to import data for ${account.username}`);
        setLoadingStates((prev) => ({ ...prev, [account.id]: false }));
      },
    });
  };

  const rows: DataTableRow[] =
    accounts.results?.map((account: EnergyLinkAccount) => ({
      id: account.id.toString(),
      username: account.username,
      actions: (
        <div className={styles.actionsContainer}>
          <div className={styles.selectContainer}>
            <Select
              id={`days-${account.id}`}
              labelText="Import last"
              value={selectedDays[account.id] || 90}
              onChange={(e) =>
                handleDaysChange(account.id, Number(e.target.value))
              }
              size="md"
              className={styles.select}
            >
              {[7, 30, 90].map((days) => (
                <SelectItem key={days} value={days} text={`${days} days`} />
              ))}
            </Select>
          </div>
          <div className={styles.buttonContainer}>
            <Button
              size="md"
              onClick={() => handleImport(account)}
              disabled={loadingStates[account.id]}
              className={styles.importButton}
            >
              {loadingStates[account.id] ? 'Importing...' : 'Import'}
            </Button>
          </div>
        </div>
      ),
    })) || [];

  if (accounts.loading) {
    return <Loading small />;
  }

  return (
    <EnergyLinkAccessCheck>
      <Helmet>
        <title>{pageTitle('Energy Link Accounts')}</title>
      </Helmet>
      <PageHeader
        breadcrumbs={[
          ['/energy_link', 'Energy Link'],
          ['/energy_link/accounts', 'Accounts'],
        ]}
        title="Energy Link Accounts"
        actions={
          <Button
            size="md"
            renderIcon={Add}
            onClick={() => history.push(routes.energyLinkNewAccountPath)}
            className={styles.addButton}
          >
            Add New Account
          </Button>
        }
      />
      {error && (
        <div className={styles.notification}>
          <InlineNotification
            kind="error"
            title="Import Failed"
            subtitle={error}
            onClose={() => {
              setError(null);
              return true;
            }}
          />
        </div>
      )}
      <DataTable rows={rows} headers={headers}>
        {({
          rows,
          headers,
          getHeaderProps,
          getRowProps,
          getTableProps,
        }: DataTableRenderProps<any, any[]>) => (
          <Table {...getTableProps()} size="lg">
            <TableHead>
              <TableRow>
                {headers.map((header: any) => (
                  <TableHeader {...getHeaderProps({ header })} key={header.key}>
                    {header.header}
                  </TableHeader>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row: any) => (
                <TableRow {...getRowProps({ row })} key={row.id}>
                  {row.cells.map((cell: DataTableCell) => (
                    <TableCell key={cell.id}>{cell.value}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DataTable>
    </EnergyLinkAccessCheck>
  );
};

export { EnergyLinkAccountsPage };
